/**
 * Demo Bookings Admin Page – /demo-bookings
 * Lists all Seatrade demo booking requests.
 * Protected: only accessible when logged in.
 */
import { useLocaleFormat } from "@/hooks/useLocaleFormat";
import apiClient from "@/services/api/apiClient";
import {
  Building2,
  Calendar,
  ChevronDown,
  ChevronUp,
  Clock,
  Mail,
  MapPin,
  RefreshCw,
  User,
  Users,
} from "lucide-react";
import React, { useEffect, useState } from "react";

interface Booking {
  _id: string;
  name: string;
  organisation: string;
  role: string;
  email: string;
  preferredTime: string;
  location: string;
  message?: string;
  createdAt: string;
  source?: string;
}

export const DemoBookingsPage: React.FC = () => {
  const localeFormat = useLocaleFormat();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await apiClient.get("/demo/bookings");
      setBookings(res.data.data.bookings || []);
    } catch {
      setError("Failed to load bookings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggle = (id: string) =>
    setExpandedId((prev) => (prev === id ? null : id));

  const fmt = (iso: string) => localeFormat.dateTime(iso);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div
        className="text-white py-10 px-4"
        style={{
          background:
            "linear-gradient(135deg, #1d4ed8 0%, #1e40af 50%, #7c3aed 100%)",
        }}
      >
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div>
            <div className="text-blue-200 text-sm font-medium mb-1 flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5" />
              Seatrade Cruise – Lufthansa Industry Booth
            </div>
            <h1 className="text-2xl font-bold">Demo Booking Requests</h1>
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Stats bar */}
        <div className="bg-white rounded-xl border border-gray-200 px-6 py-4 mb-6 flex items-center gap-6">
          <div>
            <div className="text-2xl font-bold text-gray-900">
              {bookings.length}
            </div>
            <div className="text-xs text-gray-500">Total Requests</div>
          </div>
          <div className="h-8 w-px bg-gray-200" />
          <div className="text-sm text-gray-500">
            {bookings.length > 0
              ? `Last received: ${fmt(bookings[0]!.createdAt)}`
              : "No bookings yet"}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 mb-6 text-sm">
            {error}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* Empty */}
        {!loading && !error && bookings.length === 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <Calendar className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">No demo bookings yet.</p>
          </div>
        )}

        {/* List */}
        {!loading && bookings.length > 0 && (
          <div className="space-y-3">
            {bookings.map((b, i) => (
              <div
                key={b._id}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden"
              >
                {/* Row */}
                <button
                  onClick={() => toggle(b._id)}
                  className="w-full text-left px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-4 min-w-0">
                    {/* Index badge */}
                    <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-50 text-blue-700 text-xs font-bold flex items-center justify-center">
                      {bookings.length - i}
                    </span>

                    <div className="min-w-0">
                      <div className="font-semibold text-gray-900 text-sm truncate">
                        {b.name}
                      </div>
                      <div className="text-xs text-gray-500 truncate flex items-center gap-1 mt-0.5">
                        <Building2 className="w-3 h-3 flex-shrink-0" />
                        {b.organisation}
                        <span className="text-gray-300 mx-1">·</span>
                        {b.role}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-5 flex-shrink-0 ml-4">
                    <div className="hidden sm:flex items-center gap-1.5 text-xs text-gray-500 bg-blue-50 text-blue-700 rounded-full px-3 py-1">
                      <Clock className="w-3 h-3" />
                      {b.preferredTime}
                    </div>
                    <div className="text-xs text-gray-400">
                      {fmt(b.createdAt)}
                    </div>
                    {expandedId === b._id ? (
                      <ChevronUp className="w-4 h-4 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    )}
                  </div>
                </button>

                {/* Detail panel */}
                {expandedId === b._id && (
                  <div className="border-t border-gray-100 px-6 py-5 bg-gray-50">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Detail icon={User} label="Name" value={b.name} />
                      <Detail
                        icon={Building2}
                        label="Organisation"
                        value={b.organisation}
                      />
                      <Detail icon={Users} label="Role" value={b.role} />
                      <Detail
                        icon={Mail}
                        label="Email"
                        value={
                          <a
                            href={`mailto:${b.email}`}
                            className="text-blue-600 hover:underline"
                          >
                            {b.email}
                          </a>
                        }
                      />
                      <Detail
                        icon={Clock}
                        label="Preferred Time"
                        value={b.preferredTime}
                      />
                      <Detail
                        icon={MapPin}
                        label="Location"
                        value={b.location}
                      />
                    </div>
                    {b.message && (
                      <div className="mt-4 p-3 bg-white rounded-lg border border-gray-200 text-sm text-gray-700">
                        <div className="text-xs text-gray-400 font-medium mb-1 uppercase tracking-wide">
                          Message
                        </div>
                        {b.message}
                      </div>
                    )}
                    <div className="mt-4 flex gap-3">
                      <a
                        href={`mailto:${b.email}?subject=Your HEALTHPASS Demo at Seatrade&body=Dear ${b.name},%0A%0AThank you for your interest in a HEALTHPASS demo.%0A%0AWe're confirming your slot: ${b.preferredTime}%0ALocation: ${b.location}%0A%0ABest regards,%0AThe HEALTHPASS Team`}
                        className="inline-flex items-center gap-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors font-medium"
                      >
                        <Mail className="w-3.5 h-3.5" />
                        Send Confirmation
                      </a>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Helper components ─────────────────────────────────────────────────────────

const Detail: React.FC<{
  icon: React.FC<{ className?: string }>;
  label: string;
  value: React.ReactNode;
}> = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-2.5">
    <Icon className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
    <div>
      <div className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-0.5">
        {label}
      </div>
      <div className="text-sm text-gray-800">{value}</div>
    </div>
  </div>
);
